import type { User } from "firebase/auth";
import { auth } from "../firebase";

/**
 * 閲覧プロキシ(§4.1)用のセッションクッキーを張る。
 *
 * iframe / <video> のリクエストには Authorization ヘッダを付けられないので、
 * クッキーが唯一の認証手段になる。Firebase Hosting が Cloud Run へ転送する
 * クッキーは `__session` だけなので、その名前で HttpOnly クッキーを発行させる。
 */
export async function ensureSession(user: User): Promise<void> {
  const idToken = await user.getIdToken();
  const res = await fetch("/d/_session", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ idToken }),
    credentials: "same-origin",
  });
  if (!res.ok) {
    throw new Error(`セッションの確立に失敗しました (${res.status})`);
  }
}

/**
 * 直近の ensureSession の実行。ログイン直後に画面が先に fetch を投げると
 * クッキーが未発行で 401 になる(実測: viewer のログに 401 が散発していた)。
 * 画面側はこれを await してから取りに行く。
 */
let pending: Promise<void> | null = null;

export function trackSession(p: Promise<void>): Promise<void> {
  pending = p.catch(() => undefined);
  return pending;
}

/** セッションが張り終わるまで待つ。まだ誰も張っていなければ即座に戻る */
export async function waitForSession(): Promise<void> {
  if (pending) await pending;
}

/**
 * 認可プロキシから取得する。セッション確立を待ち、それでも 401 なら
 * クッキーを張り直して1度だけやり直す(期限切れ・別タブでのログアウト対策)。
 */
export async function fetchFromProxy(path: string): Promise<Response> {
  await waitForSession();
  const first = await fetch(path, { credentials: "same-origin" });
  if (first.status !== 401) return first;

  const user = auth.currentUser;
  if (!user) return first;
  await trackSession(ensureSession(user));
  return fetch(path, { credentials: "same-origin" });
}
